import { useEffect, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Circle,
  Popup,
  ZoomControl,
} from "react-leaflet";
import { Box, Button } from "@mui/material";
import "leaflet/dist/leaflet.css";
import type { AQIData, MapPosition, MapProps } from "../types";
import { generateColorFromLatLong } from "../utils/aqi";
import LocateMeButton from "./LocateMeButton";
import MetricGraph from "./MetricGraph";
import FlyToLocation from "./FlyToLocation";
import SensorDetails from "./SensorDetails";
import { getGraphData } from "../utils/requests";
import DataLayerSelector from "./DataLayerSelector";
import PredictionGraph from "./PredictionGraph";
import HistogramPrediction from "./HistogramPrediction";

export default function Map({
  aqiData,
  searchPosition,
  selectedModel,
  showPredictionGraph,
  setShowPredictionGraph,
}: MapProps) {
  const [position, setPosition] = useState<MapPosition>({
    lat: 47.8064,
    lng: -122.317,
  });

  const [showGraph, setShowGraph] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState<AQIData | null>(null);
  const [selectedIDOption, setSelectedIDOption] = useState<string | "none">(
    "none"
  );
  const [isGraphDisabled, setIsGraphDisabled] = useState(false);
  const [isFetchingMetricData, setIsFetchingMetricData] = useState(false);
  const [mapData, setMapData] = useState([]);
  const [selectedLayer, setSelectedLayer] = useState("FM");

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPosition({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      () => {
        console.log("Using default location");
      }
    );
  }, []);

  useEffect(() => {
    setIsGraphDisabled(selectedIDOption === "none");
  }, [selectedIDOption]);

  const handleSensorSelect = (sensor: AQIData) => {
    setSelectedSensor(sensor);
    setSelectedIDOption("none"); // Reset the ID when switching sensors
  };

  const handleGraph = async () => {
    setIsFetchingMetricData(true);
    setShowGraph(true);

    try {
      const data = await getGraphData(selectedIDOption, selectedLayer);
      setMapData(data);
    } catch (error) {
      console.error("Error fetching graph data:", error);
    }

    setIsFetchingMetricData(false);
  };

  return (
    <>
      <MapContainer
        center={[position.lat, position.lng]}
        zoom={11}
        style={{ width: "100%", height: "100%" }}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <ZoomControl position="bottomright" />

        {searchPosition && <FlyToLocation searchPosition={searchPosition} />}

        {aqiData.map((sensor) => (
          <Circle
            key={sensor.AQS_Site_ID}
            center={[sensor.Latitude, sensor.Longitude]}
            radius={1000}
            pathOptions={{
              color: generateColorFromLatLong(
                sensor.Latitude,
                sensor.Longitude
              ),
              fillColor: generateColorFromLatLong(
                sensor.Latitude,
                sensor.Longitude
              ),
              fillOpacity: 0.8,
            }}
            eventHandlers={{
              click: () => handleSensorSelect(sensor),
            }}
          >
            <Popup minWidth={300}>
              <SensorDetails
                key={sensor.AQS_Site_ID}
                sensor={sensor}
                selectedIDOption={selectedIDOption}
                setSelectedIDOption={setSelectedIDOption}
              />
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  disabled={isGraphDisabled}
                  onClick={handleGraph}
                >
                  Show Graph
                </Button>
              </Box>
            </Popup>
          </Circle>
        ))}

        {showGraph && selectedSensor && (
          <MetricGraph
            isFetchingMetricData={isFetchingMetricData}
            metricData={mapData}
            onClose={() => setShowGraph(false)}
          />
        )}

        <LocateMeButton />
        <DataLayerSelector
          selectedLayer={selectedLayer}
          setSelectedLayer={setSelectedLayer}
        />
        {showPredictionGraph && selectedModel && (
          // <PredictionGraph
          //   selectedModel={selectedModel}
          //   onClose={() => setShowPredictionGraph(false)}
          // />
          <HistogramPrediction
            selectedModel={selectedModel}
            onClose={() => setShowPredictionGraph(false)}
          />
        )}
      </MapContainer>
    </>
  );
}
